import Button from "../ui/button/Button";
import { NMWellCard } from "../nm";
import { NMDocumentList } from "../nm";
import { NMWellData } from "../../lib/api/nm";
import { W3AInitialResponse, NMDocumentForPlan } from "../../lib/api/plans";

interface NMDocumentsStepProps {
  initialResponse: W3AInitialResponse;
  onConfirm: () => void;
  onBack: () => void;
  isLoading?: boolean;
}

/**
 * NM-specific Step 2 component for document verification.
 * Shows external OCD portal link instead of local PDF preview,
 * displays scraped well data via NMWellCard, and shows document list.
 */
export default function NMDocumentsStep({
  initialResponse,
  onConfirm,
  onBack,
  isLoading = false,
}: NMDocumentsStepProps) {
  // Convert NMWellDataForPlan to the format expected by NMWellCard
  const wellData = initialResponse.well_data;
  const documents = initialResponse.nm_documents || [];

  // Map NMDocumentForPlan to NMDocument format for the list component
  const mappedDocuments = documents.map((doc: NMDocumentForPlan) => ({
    filename: doc.filename,
    url: doc.url,
    file_size: doc.file_size,
    date: doc.date,
    doc_type: doc.doc_type,
  }));

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/20">
            <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              New Mexico Well Data
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Data scraped from NM OCD portal
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* NM Data Availability Banner - conditional based on scraped data */}
        {(() => {
          const hasCasing = wellData?.casing_records && wellData.casing_records.length > 0;
          const hasPerforations = wellData?.completions?.some(
            (c: { perforations?: unknown[] }) => c.perforations && c.perforations.length > 0
          );

          if (hasCasing || hasPerforations) {
            return (
              <div className="rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 p-4">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-green-900 dark:text-green-100 text-sm">
                      Casing & Perforation Data Found
                    </h4>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      {hasCasing && `${wellData.casing_records?.length} casing string(s)`}
                      {hasCasing && hasPerforations && " and "}
                      {hasPerforations && "perforation interval(s)"}
                      {" "}scraped from OCD portal. You can review and edit in the Geometry step.
                    </p>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-4">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h4 className="font-semibold text-amber-900 dark:text-amber-100 text-sm">
                    NM Well - Manual Entry Required
                  </h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Casing strings and perforations were not found in the OCD portal for this well and must be entered manually in the Geometry step.
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* External PDF Link */}
        {initialResponse.combined_pdf_url && (
          <div className="border border-gray-200 rounded-lg p-6 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/20 mb-2">
              <svg className="w-8 h-8 text-brand-600 dark:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 mb-1">
                OCD Document Portal
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                View documents on the NM OCD imaging portal
              </p>
            </div>

            <a
              href={initialResponse.combined_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Open at OCD Portal
            </a>

            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              Documents are hosted externally at the NM OCD imaging portal
            </p>
          </div>
        )}

        {/* Well Data Card */}
        {wellData && (
          <NMWellCard
            well={wellData as unknown as NMWellData}
          />
        )}

        {/* Document List */}
        {documents.length > 0 && (
          <NMDocumentList
            api={initialResponse.api}
            documents={mappedDocuments}
          />
        )}

        {/* No data fallback */}
        {!wellData && documents.length === 0 && (
          <div className="border border-gray-200 rounded-lg p-8 dark:border-gray-700 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No well data available. The well may not exist in the OCD system.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={onBack}
            disabled={isLoading}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Back
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : "Confirm & Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
